import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';

import { exportRegister } from './register-export.js';
import {
  ApiError,
  COMMITMENT_SOURCES,
  createCommitment,
  createCostEntry,
  deleteCommitment,
  deleteCostEntry,
  getCosts,
  importCostEntries,
  listBudget,
  listVariations,
  type BudgetLine,
  type CostReportLine,
  type CostView,
  type ImportReport,
  type Variation,
} from '../api.js';
import { formatDate, formatMoney } from '../labels.js';
import { CrossLink, focusClass, useFocusRow } from './cross-links.js';
import {
  Drawer,
  RegisterToolbar,
  RowActions,
  matches,
  useRegisterView,
} from './register-kit.js';

/**
 * The cost report (CM-02/CM-03) — the screen a QS lives in.
 *
 * Four numbers per budget line, and the whole point is the fourth:
 *
 *   Allowed     what the budget permits
 *   Committed   POs and subcontracts signed — money gone, invoice or not
 *   Spent       what has actually been paid
 *   Forecast    the larger of "allowed" and "committed + spent"
 *
 * A line with 100k allowed, 90k committed and 20k spent is NOT on budget. The
 * spend alone looks comfortable; 110k is already gone. That is the case the
 * report exists to catch, and it is why the forecast column says WHERE its
 * number came from — a QS who cannot see that cannot argue with it.
 *
 * WHY THE COLUMNS ARE IN THAT ORDER
 * ---------------------------------
 * Left to right is the order the money moves: permitted, promised, paid,
 * expected. Somebody reading across a row is following one trade through time.
 *
 * ON THE REGISTER PATTERN — this screen is one report and TWO registers
 * --------------------------------------------------------------------
 * The report at the top is a SUMMARY, one row per budget line with a total
 * underneath. It gets no toolbar: filtering a six-line summary hides part of a
 * total and leaves the total saying something else, which is worse than
 * scrolling. It keeps its shape, and the export button beside it takes the
 * whole thing.
 *
 * The two ledgers below it ARE registers — a job imports hundreds of cost rows
 * from a CSV — so each gets its own search, sort, density and export. Their one
 * row action is Remove, and it stays in the overflow menu rather than on the
 * row: deleting a cost entry is not something anybody should be one click from.
 */

const SOURCE_LABELS: Readonly<Record<string, string>> = {
  purchase_order: 'Purchase order',
  subcontract: 'Subcontract',
  other: 'Other',
};

/**
 * The overspend on a line, or an em-dash when there is none.
 *
 * `variance` is `allowed − forecast`, which is zero within budget and negative
 * over it. Shown as a MAGNITUDE under a column called "Over by", because
 * "Over by −70,000.00" is two negatives to unpick before the reader knows
 * whether that is good or bad.
 *
 * The minus is stripped by string surgery and never by `Number()`: money is a
 * decimal string from the database column to the screen, and a float in the
 * middle of that journey is how 1,250,000.00 quietly becomes 1,249,999.99.
 */
function overBy(variance: string): string {
  return variance.startsWith('-') ? formatMoney(variance.slice(1)) : '—';
}

interface Props {
  readonly projectId: string;
  readonly currency: string;
  readonly can: {
    readonly write: boolean;
    readonly remove: boolean;
    /** `cost: X`. Gates the buttons; the rows are already permission-filtered. */
    readonly exportRows: boolean;
  };
  /** An operator support visit that did not ask for the money (ADR-041). */
  readonly visiting?: boolean;
}

/** What the drawer is doing. `null` means it is shut. */
type DrawerMode = 'commitment' | 'cost' | 'import';

export function Costs({ projectId, currency, can, visiting = false }: Props): JSX.Element {
  const [view, setView] = useState<CostView | null>(null);
  const [budgetLines, setBudgetLines] = useState<readonly BudgetLine[]>([]);
  /** Not the same as an empty report — see `Drawings.tsx`. */
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [drawer, setDrawer] = useState<DrawerMode | null>(null);
  /** Set by the form inside the drawer, so closing it can ask before discarding. */
  const [formDirty, setFormDirty] = useState(false);
  /**
   * The variations, so a cost whose reference quotes a VO number can link back
   * to it. Same posture as the budget lines below: a failure loses the link,
   * never the report.
   */
  const [variations, setVariations] = useState<readonly Variation[]>([]);

  const load = useCallback(async () => {
    try {
      setView(await getCosts(projectId));
      setDenied(false);
      // The budget is the same money and the same permission, so a failure here
      // is not worth its own message — the picker just offers no categories.
      try {
        setBudgetLines((await listBudget(projectId)).lines);
      } catch {
        setBudgetLines([]);
      }
      try {
        setVariations((await listVariations(projectId)).variations);
      } catch {
        setVariations([]);
      }
    } catch (caught) {
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load the cost report.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(work: () => Promise<CostView>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setView(await work());
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** Shut the drawer, and forget whether its form had edits in it. */
  const closeDrawer = useCallback(() => {
    setDrawer(null);
    setFormDirty(false);
    setError(null);
  }, []);

  /**
   * The report, as the QS would take it to a meeting.
   *
   * The totals row is in the file too. A cost report without its totals is a
   * spreadsheet somebody has to add up again, and adding it up again is where
   * the number stops matching the one on screen.
   */
  async function exportReport(): Promise<void> {
    if (!view) return;
    const headers = [
      'Line',
      `Allowed (${currency})`,
      'Committed',
      'Spent',
      'Forecast',
      // The same word as the column, and the same magnitude — a QS who exports
      // what is on screen should not have to work out that the sign flipped.
      // Zero rather than blank, so the column still sums to the total overspend.
      'Over by',
      'Heading over budget',
      'Forecast from',
    ];
    const overspend = (variance: string): string =>
      variance.startsWith('-') ? variance.slice(1) : '0';
    const rows: string[][] = view.report.lines.map((line) => [
      line.category,
      line.allowed,
      line.committed,
      line.spent,
      line.forecast,
      overspend(line.variance),
      line.over ? 'Yes' : '',
      line.forecast_from === 'committed_and_spent' ? 'What is committed and spent' : 'The budget',
    ]);
    rows.push([
      'Total',
      view.report.total_allowed,
      view.report.total_committed,
      view.report.total_spent,
      view.report.total_forecast,
      overspend(view.report.total_variance),
      '',
      '',
    ]);
    setError(null);
    try {
      // The whole report, so there are no filters to record — but the row
      // count and the fact it happened still are (`09` §Threats).
      await exportRegister({
        projectId,
        resource: 'cost',
        filename: `cost-report-${projectId.slice(0, 8)}.csv`,
        headers,
        rows,
      });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not export.');
    }
  }

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Costs</h3>
      {view && can.write && (
        <>
          <button type="button" className="inline" onClick={() => setDrawer('commitment')}>
            Record a commitment
          </button>
          <button type="button" className="inline" onClick={() => setDrawer('cost')}>
            Record a cost
          </button>
          <button type="button" className="inline" onClick={() => setDrawer('import')}>
            Import a CSV
          </button>
        </>
      )}
      {view && can.exportRows && view.report.lines.length > 0 && (
        <button type="button" className="secondary inline" onClick={() => void exportReport()}>
          Export the report to CSV
        </button>
      )}
    </div>
  );

  if (denied) {
    return (
      <>
        {head}
        <p className="hint">
          {visiting
            ? 'This visit cannot see money, so the cost report is not shown.'
            : 'You do not have financial access on this project, so the cost report is not shown.'}
        </p>
      </>
    );
  }

  if (!view) {
    return (
      <>
        {head}
        {error ? (
          <div className="alert" role="alert">
            <span>{error}</span>
          </div>
        ) : (
          <p aria-live="polite">Loading the cost report…</p>
        )}
      </>
    );
  }

  const { report } = view;

  return (
    <>
      {head}

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {/*
        Said before the table, because it is the sentence somebody repeats in a
        meeting. A total can look fine while one trade is badly over, so the
        two facts are stated separately.
      */}
      {report.lines.length > 0 && (
        <div
          className={`panel exposure exposure-${report.any_line_over ? 'red' : 'green'}`}
          role="status"
        >
          {report.any_line_over ? (
            <p>
              <strong>
                {report.lines.filter((l) => l.over).length} of {report.lines.length} lines are
                heading over budget.
              </strong>{' '}
              Forecast {currency} {formatMoney(report.total_forecast)} against{' '}
              {formatMoney(report.total_allowed)} allowed.
            </p>
          ) : (
            <p>
              <strong>Every line is within its budget.</strong> Forecast {currency}{' '}
              {formatMoney(report.total_forecast)} against {formatMoney(report.total_allowed)}{' '}
              allowed.
            </p>
          )}
          <p className="hint">
            A commitment is money gone whether or not the invoice has arrived, so the forecast is
            the larger of the budget and what has been committed plus spent.
          </p>
        </div>
      )}

      {report.lines.length === 0 ? (
        <div className="empty">
          <p>
            <strong>Nothing to report yet.</strong>
          </p>
          <p>
            {can.write
              ? 'Set a budget first, then record the POs and subcontracts against it. The forecast follows from those.'
              : /*
                 * An empty state must name an action ITS READER can take. A
                 * Company Admin holds `budget: VX` and `cost: VX` — read and
                 * export, no create — so the sentence for them says who does it.
                 *
                 * IT SAID "OR A DIRECTOR", WHICH WAS FALSE. A Director holds
                 * `budget: g('VX')` and `cost: g('VX')` — exactly what the
                 * admin reading this holds. By default `qs_commercial` is the
                 * only role that may create either. Naming the Director sent
                 * the reader to somebody equally unable to help.
                 *
                 * It points at Company → Roles rather than a role, because
                 * since ADR-035 the answer is per company and any role named
                 * in this string is a copy of the matrix that will go stale.
                 */
                'No budget has been set, and nothing has been committed or spent. By default only QS / Commercial may record these — Company → Roles decides who else in your company can.'}
          </p>
        </div>
      ) : (
        <div className="rk-scroll">
          <table className="grid rk-cards">
            <caption className="visually-hidden">
              Budget, committed, spent and forecast for each line
            </caption>
            <thead>
              <tr>
                <th scope="col">Line</th>
                <th scope="col">Allowed ({currency})</th>
                <th scope="col">Committed</th>
                <th scope="col">Spent</th>
                <th scope="col">Forecast</th>
                {/*
                  "OVER BY", NOT "LEFT".
                  ----------------------
                  The number is `allowed − forecast`, and the forecast is the
                  LARGER of the allowance and what is committed plus spent — so
                  it is zero on every line within budget and negative on every
                  line that is not. It can never be positive.

                  Called "Left" it read as budget remaining, and an untouched
                  500,000 line said "Left 0.00" — the opposite of the truth, on
                  the screen a QS trusts with money. The arithmetic is right and
                  deliberate; the word was wrong.
                */}
                <th scope="col">Over by</th>
              </tr>
            </thead>
            <tbody>
              {report.lines.map((line) => (
                <ReportRow key={line.budget_line_id ?? 'uncategorised'} line={line} />
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">Total</th>
                <td data-label="Allowed">{formatMoney(report.total_allowed)}</td>
                <td data-label="Committed">{formatMoney(report.total_committed)}</td>
                <td data-label="Spent">{formatMoney(report.total_spent)}</td>
                <td data-label="Forecast">{formatMoney(report.total_forecast)}</td>
                <td
                  data-label="Over by"
                  className={report.total_variance.startsWith('-') ? 'task-overdue' : undefined}
                >
                  {overBy(report.total_variance)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <Ledger
        projectId={projectId}
        title="Commitments"
        noun={['commitment', 'commitments']}
        csvName={`commitments-${projectId.slice(0, 8)}.csv`}
        empty={
          can.write
            ? 'No POs or subcontracts recorded. Record each one as it is signed, so the forecast reflects what is already promised.'
            : 'No POs or subcontracts recorded. By default only QS / Commercial may record them — Company → Roles decides who else in your company can.'
        }
        rows={view.commitments.map((c) => ({
          id: c.id,
          left: c.reference,
          detail: [SOURCE_LABELS[c.source] ?? c.source, c.budget_category, c.description]
            .filter(Boolean)
            .join(' · '),
          amount: c.amount,
          date: c.committed_on,
          who: c.created_by_name,
        }))}
        canRemove={can.remove}
        canExport={can.exportRows}
        onError={setError}
        busy={busy}
        onRemove={(id) => void run(() => deleteCommitment(projectId, id))}
      />

      <Ledger
        projectId={projectId}
        title="Costs"
        noun={['cost', 'costs']}
        csvName={`costs-${projectId.slice(0, 8)}.csv`}
        empty={
          can.write
            ? 'Nothing has been recorded as spent. Record costs as the invoices arrive, or import them from a CSV.'
            : 'Nothing has been recorded as spent. By default only QS / Commercial may record it — Company → Roles decides who else in your company can.'
        }
        rows={view.costs.map((c) => {
          const variation = variations.find((v) => v.number === c.reference?.trim());
          return {
            id: c.id,
            left: c.description,
            detail: [c.budget_category, c.reference, c.source === 'csv_import' ? 'imported' : null]
              .filter(Boolean)
              .join(' · '),
            amount: c.amount,
            date: c.cost_date,
            who: c.created_by_name,
            ...(variation ? { variation: { id: variation.id, number: variation.number } } : {}),
          };
        })}
        canRemove={can.remove}
        canExport={can.exportRows}
        onError={setError}
        busy={busy}
        onRemove={(id) => void run(() => deleteCostEntry(projectId, id))}
      />

      {drawer === 'commitment' && (
        <Drawer title="Record a commitment" dirty={formDirty} onClose={closeDrawer}>
          <CommitmentForm
            currency={currency}
            budgetLines={budgetLines}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              if (await run(() => createCommitment(projectId, input))) closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer === 'cost' && (
        <Drawer title="Record a cost" dirty={formDirty} onClose={closeDrawer}>
          <CostForm
            currency={currency}
            budgetLines={budgetLines}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              if (await run(() => createCostEntry(projectId, input))) closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer === 'import' && (
        <Drawer title="Import costs from a CSV" onClose={closeDrawer}>
          <ImportForm
            projectId={projectId}
            onCancel={closeDrawer}
            onImported={(imported, hasSomethingToSay) => {
              setView(imported);
              // Stays open when there is something the import has to TELL them —
              // which categories went in without a budget line. Closing on
              // success would destroy that message the instant it appeared, and
              // the rows would be uncategorised with nobody any the wiser.
              if (!hasSomethingToSay) closeDrawer();
            }}
          />
        </Drawer>
      )}
    </>
  );
}


// ---------------------------------------------------------------------------

function ReportRow({ line }: { readonly line: CostReportLine }): JSX.Element {
  return (
    <tr className={line.over ? 'vo-exposed' : undefined}>
      <th scope="row">
        {line.category}
        {/*
          Never colour alone. A row heading over says so in words, and says how
          the forecast got there — "from the figures" is the difference between
          a projection and a fact.
        */}
        {line.over && <span className="task-overdue">{' · '}Heading over budget</span>}
        {line.forecast_from === 'committed_and_spent' && (
          <span className="snag-note">Forecast from what is committed and spent</span>
        )}
      </th>
      <td data-label="Allowed">{formatMoney(line.allowed)}</td>
      <td data-label="Committed">{formatMoney(line.committed)}</td>
      <td data-label="Spent">{formatMoney(line.spent)}</td>
      <td data-label="Forecast">{formatMoney(line.forecast)}</td>
      <td data-label="Over by" className={line.over ? 'task-overdue' : undefined}>
        {overBy(line.variance)}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------

interface LedgerRow {
  readonly id: string;
  readonly left: string;
  readonly detail: string;
  readonly amount: string;
  readonly date: string;
  readonly who: string | null;
  /** The variation whose number this row's reference matches, if any. */
  readonly variation?: { readonly id: string; readonly number: string };
}

function Ledger({
  projectId,
  title,
  noun,
  csvName,
  empty,
  rows,
  canRemove,
  canExport,
  busy,
  onRemove,
  onError,
}: {
  readonly projectId: string;
  readonly title: string;
  readonly noun: readonly [string, string];
  readonly csvName: string;
  readonly empty: string;
  readonly rows: readonly LedgerRow[];
  readonly canRemove: boolean;
  readonly canExport: boolean;
  readonly busy: boolean;
  readonly onRemove: (id: string) => void;
  /** A refused or failed export. Shown on the Costs page, not under the ledger. */
  readonly onError: (message: string) => void;
}): JSX.Element {
  // Each ledger keeps its OWN search and sort — the two are different lists and
  // somebody looking for a PO is not looking for an invoice. Density is shared,
  // because it lives in one `localStorage` key for the whole product.
  const view = useRegisterView('date');

  const shown = useMemo(() => {
    const filtered = rows.filter((row) => matches(`${row.left} ${row.detail}`, view.query));

    const compare: Readonly<Record<string, (a: LedgerRow, b: LedgerRow) => number>> = {
      // Newest first: a ledger is read from the most recent thing backwards.
      date: (a, b) => (a.date === b.date ? 0 : a.date < b.date ? 1 : -1),
      // Largest first, and by absolute size, so a big credit note sorts beside
      // the big invoice it is cancelling rather than at the far end.
      amount: (a, b) => Math.abs(Number(b.amount)) - Math.abs(Number(a.amount)),
      what: (a, b) => a.left.localeCompare(b.left),
    };

    return [...filtered].sort(compare[view.sortKey] ?? compare.date!);
  }, [rows, view.query, view.sortKey]);

  async function exportRows(): Promise<void> {
    const headers = ['What', 'Detail', 'Amount', 'Date', 'Recorded by'];
    const body = shown.map((row) => [
      row.left,
      row.detail,
      row.amount,
      row.date,
      row.who ?? '',
    ]);
    try {
      await exportRegister({
        projectId,
        resource: 'cost',
        filename: csvName,
        headers,
        rows: body,
        filters: { ledger: title, query: view.query },
      });
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : 'That did not export.');
    }
  }

  if (rows.length === 0) {
    return (
      <>
        <p className="detail-label">{title}</p>
        <p className="hint">{empty}</p>
      </>
    );
  }

  return (
    <>
      <p className="detail-label">{title}</p>

      <RegisterToolbar
        view={view}
        searchLabel={`Search ${title.toLowerCase()}`}
        searchPlaceholder="Reference, description, category"
        shown={shown.length}
        total={rows.length}
        noun={noun}
        sorts={[
          { key: 'date', label: 'Most recent' },
          { key: 'amount', label: 'Largest' },
          { key: 'what', label: 'What it is' },
        ]}
      >
        {canExport && (
          <button type="button" className="secondary inline" onClick={() => void exportRows()}>
            Export {shown.length === rows.length ? 'all' : 'these'} to CSV
          </button>
        )}
      </RegisterToolbar>

      {shown.length === 0 ? (
        /*
         * A DIFFERENT empty state from "nothing recorded". This reader has a
         * ledger of several hundred imported rows and a search that matches
         * none of them.
         */
        <div className="empty">
          <p>
            <strong>Nothing matches that.</strong>
          </p>
          <p>
            <button type="button" className="link-button" onClick={view.clear}>
              Clear the search
            </button>{' '}
            to see all {rows.length}.
          </p>
        </div>
      ) : (
        <div className={`rk-scroll${view.density === 'compact' ? ' rk-compact' : ''}`}>
          <table className="grid rk-cards">
            <caption className="visually-hidden">
              {title}. {shown.length} of {rows.length} shown.
            </caption>
            <thead>
              <tr>
                <th scope="col">What</th>
                <th scope="col">Amount</th>
                <th scope="col">Date</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <LedgerRowEl key={row.id} row={row}>
                  <th scope="row">
                    {row.left}
                    <span className="rk-secondary">
                      {row.detail && <span className="snag-note">{row.detail}</span>}
                      {/*
                        Back to the variation whose number was typed as this
                        row's reference. The words name the match, because there
                        is no foreign key here — see `SpentAgainst` in
                        `Variations.tsx`.
                      */}
                      {row.variation && projectId && (
                        <span className="snag-note">
                          Reference matches{' '}
                          <CrossLink
                            projectId={projectId}
                            section="commercial"
                            focus={row.variation.id}
                            says="the variation whose number this cost quotes as its reference"
                          >
                            {row.variation.number}
                          </CrossLink>
                        </span>
                      )}
                    </span>
                  </th>
                  {/* A credit note is negative and says so rather than hiding it. */}
                  <td
                    data-label="Amount"
                    className={row.amount.startsWith('-') ? 'hint' : undefined}
                  >
                    {formatMoney(row.amount)}
                    {/* Separator inside the span — the Date cell below already
                        had it. Without it this read "-1,200.00credit". */}
                    {row.amount.startsWith('-') && (
                      <span className="tree-code">{' · '}credit</span>
                    )}
                  </td>
                  <td data-label="Date">
                    {formatDate(row.date)}
                    {row.who && <span className="hint"> · {row.who}</span>}
                  </td>
                  <td>
                    {canRemove && (
                      // Remove is the only action, and it is destructive, so it
                      // stays behind the menu rather than sitting on the row.
                      <RowActions
                        rowLabel={row.left}
                        more={[
                          {
                            label: 'Remove',
                            danger: true,
                            disabled: busy,
                            onSelect: () => onRemove(row.id),
                          },
                        ]}
                      />
                    )}
                  </td>
                </LedgerRowEl>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/** One ledger row, which knows whether a cross-link came looking for it. */
function LedgerRowEl({
  row,
  children,
}: {
  readonly row: LedgerRow;
  readonly children: React.ReactNode;
}): JSX.Element {
  const { focused, ref } = useFocusRow<HTMLTableRowElement>(row.id);
  return (
    <tr ref={ref} className={focusClass(focused)} aria-current={focused ? 'true' : undefined}>
      {children}
    </tr>
  );
}

// ---------------------------------------------------------------------------

const MONEY_OK = /^-?\d+(\.\d{1,4})?$/;

function BudgetPicker({
  id,
  value,
  lines,
  busy,
  onChange,
}: {
  readonly id: string;
  readonly value: string;
  readonly lines: readonly BudgetLine[];
  readonly busy: boolean;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  return (
    <div className="field">
      <label htmlFor={id}>Against which budget line?</label>
      <select id={id} value={value} disabled={busy} onChange={(e) => onChange(e.target.value)}>
        {/*
          "None" is a real answer, not a placeholder. A PO signed before the
          budget was cut up still has to be recordable, and it then shows on
          the report as uncategorised rather than disappearing.
        */}
        <option value="">Not against one</option>
        {lines.map((line) => (
          <option key={line.id} value={line.id}>
            {line.category}
          </option>
        ))}
      </select>
    </div>
  );
}

function CommitmentForm({
  currency,
  budgetLines,
  busy,
  onCancel,
  onDirtyChange,
  onSubmit,
}: {
  readonly currency: string;
  readonly budgetLines: readonly BudgetLine[];
  readonly busy: boolean;
  readonly onCancel: () => void;
  /** Lets the drawer ask before throwing away what has been typed. */
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (input: {
    reference: string;
    description?: string;
    source: string;
    amount: string;
    committed_on: string;
    budget_line_id: string | null;
  }) => void | Promise<void>;
}): JSX.Element {
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [source, setSource] = useState('purchase_order');
  const [amount, setAmount] = useState('');
  const [on, setOn] = useState(new Date().toISOString().slice(0, 10));
  const [budgetLineId, setBudgetLineId] = useState('');

  /*
   * The date starts at today, so it is compared against that rather than
   * against nothing — otherwise the form is "dirty" the moment it opens.
   */
  useEffect(() => {
    onDirtyChange?.(
      reference !== '' ||
        description !== '' ||
        amount !== '' ||
        budgetLineId !== '' ||
        source !== 'purchase_order',
    );
  }, [reference, description, amount, budgetLineId, source, onDirtyChange]);

  const money = amount.replace(/,/g, '').trim();
  const valid =
    reference.trim().length > 0 && MONEY_OK.test(money) && !money.startsWith('-') && on !== '';

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!valid) return;
    void onSubmit({
      reference: reference.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      source,
      amount: money,
      committed_on: on,
      budget_line_id: budgetLineId || null,
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <p className="hint">
        A signed PO or subcontract. It counts against the forecast from today, whether or not an
        invoice has arrived — which is the number people miss.
      </p>

      <div className="field-row">
        <div className="field">
          <label htmlFor="cm-reference">PO or subcontract number</label>
          <input
            id="cm-reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            maxLength={120}
            disabled={busy}
            placeholder="PO-1042"
          />
        </div>
        <div className="field">
          <label htmlFor="cm-source">What kind</label>
          <select
            id="cm-source"
            value={source}
            disabled={busy}
            onChange={(e) => setSource(e.target.value)}
          >
            {COMMITMENT_SOURCES.map((key) => (
              <option key={key} value={key}>
                {SOURCE_LABELS[key] ?? key}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="cm-amount">Amount ({currency})</label>
          <input
            id="cm-amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            disabled={busy}
            placeholder="90000.00"
          />
          <p className="hint">A credit is a cost entry, not a negative commitment.</p>
        </div>
        <div className="field">
          <label htmlFor="cm-on">Committed on</label>
          <input
            id="cm-on"
            type="date"
            value={on}
            onChange={(e) => setOn(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>

      <BudgetPicker
        id="cm-line"
        value={budgetLineId}
        lines={budgetLines}
        busy={busy}
        onChange={setBudgetLineId}
      />

      <div className="field">
        <label htmlFor="cm-description">Any detail</label>
        <input
          id="cm-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={4000}
          disabled={busy}
        />
      </div>

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !valid}>
          {busy ? 'Saving…' : 'Record it'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * A month of ledger, out of the accounts package.
 *
 * Paste, not upload. Nothing keeps the file and nothing serves it back — it
 * exists only long enough to become rows — and a paste box works on a laptop
 * where the export is already on the clipboard.
 *
 * The panel's whole job is what happens when the file is NOT clean, because
 * that is most of the time on the first attempt. It never imports part of a
 * file, and it never doubles a ledger without asking.
 */
function ImportForm({
  projectId,
  onCancel,
  onImported,
}: {
  readonly projectId: string;
  readonly onCancel: () => void;
  readonly onImported: (view: CostView, hasSomethingToSay: boolean) => void;
}): JSX.Element {
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function send(allowDuplicates: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await importCostEntries(projectId, csv, { allowDuplicates });
      setReport(result);
      if (result.imported && result.view) {
        onImported(result.view, result.unknown_categories.length > 0);
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <p className="hint">
        Paste the export. The first row has to name the columns:{' '}
        <strong>Description, Amount, Date</strong> — and, if you have them,{' '}
        <strong>Reference</strong> and <strong>Category</strong>. Dates as{' '}
        <strong>YYYY-MM-DD</strong>, so there is no doubt which number is the month. A credit is a
        minus or brackets.
      </p>

      <div className="field">
        <label htmlFor="cost-csv">The file</label>
        <textarea
          id="cost-csv"
          rows={8}
          value={csv}
          disabled={busy}
          onChange={(e) => {
            setCsv(e.target.value);
            // A stale report about the previous paste is worse than none.
            setReport(null);
          }}
          placeholder={'Description,Amount,Date,Reference,Category\nSteel studs,12500.00,2026-08-01,INV-4412,Partitions'}
        />
      </div>

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {/*
        NOTHING was imported. Every line that has to change is named, so one
        pass through the file fixes it — the alternative, importing what parsed
        and listing what did not, leaves a cost report quietly short.
      */}
      {report && report.problems.length > 0 && (
        <div className="alert" role="alert">
          <p>
            <strong>Nothing was imported.</strong> {report.problems.length}{' '}
            {report.problems.length === 1 ? 'row has' : 'rows have'} a problem — fix them and paste
            it again.
          </p>
          <ul className="task-list">
            {report.problems.map((problem) => (
              <li key={problem.line} className="task-row">
                <span className="task-title">Line {problem.line}</span>
                <span className="task-description">{problem.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/*
        A question, not an error. Two identical taxi fares on one day are real,
        so there is a way to say yes — but running the same export twice doubles
        every figure, and that is the mistake this is here to catch.
      */}
      {report && !report.imported && report.problems.length === 0 && report.duplicates.length > 0 && (
        <div className="alert" role="alert">
          <p>
            <strong>Nothing was imported yet.</strong> {report.duplicates.length}{' '}
            {report.duplicates.length === 1 ? 'row looks' : 'rows look'} like{' '}
            {report.duplicates.length === 1 ? 'one' : 'ones'} you already have. If this file has
            been imported before, close this — importing it again would double those figures.
          </p>
          <ul className="task-list">
            {report.duplicates.map((duplicate) => (
              <li key={duplicate.line} className="task-row">
                <span className="task-title">Line {duplicate.line}</span>
                <span className="task-description">{duplicate.why}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {report?.imported && report.unknown_categories.length > 0 && (
        <p className="hint">
          {report.count} {report.count === 1 ? 'cost' : 'costs'} imported. These categories are not
          budget lines on this project, so those rows went in without one:{' '}
          <strong>{report.unknown_categories.join(', ')}</strong>. You can set them from the list
          below.
        </p>
      )}

      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || !csv.trim()}
          onClick={() => void send(false)}
        >
          {busy ? 'Reading…' : 'Import'}
        </button>

        {report && !report.imported && report.problems.length === 0 && report.duplicates.length > 0 && (
          <button
            type="button"
            className="inline"
            disabled={busy}
            onClick={() => void send(true)}
          >
            Import anyway — they are real
          </button>
        )}

        <button type="button" className="secondary inline" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function CostForm({
  currency,
  budgetLines,
  busy,
  onCancel,
  onDirtyChange,
  onSubmit,
}: {
  readonly currency: string;
  readonly budgetLines: readonly BudgetLine[];
  readonly busy: boolean;
  readonly onCancel: () => void;
  /** Lets the drawer ask before throwing away what has been typed. */
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (input: {
    description: string;
    reference?: string;
    amount: string;
    cost_date: string;
    budget_line_id: string | null;
  }) => void | Promise<void>;
}): JSX.Element {
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [amount, setAmount] = useState('');
  const [on, setOn] = useState(new Date().toISOString().slice(0, 10));
  const [budgetLineId, setBudgetLineId] = useState('');

  // The date starts at today, so it is not part of the comparison — see
  // `CommitmentForm`.
  useEffect(() => {
    onDirtyChange?.(
      description !== '' || reference !== '' || amount !== '' || budgetLineId !== '',
    );
  }, [description, reference, amount, budgetLineId, onDirtyChange]);

  const money = amount.replace(/,/g, '').trim();
  const valid =
    description.trim().length > 0 && MONEY_OK.test(money) && Number(money) !== 0 && on !== '';

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!valid) return;
    void onSubmit({
      description: description.trim(),
      ...(reference.trim() ? { reference: reference.trim() } : {}),
      amount: money,
      cost_date: on,
      budget_line_id: budgetLineId || null,
    });
  }

  return (
    <form className="panel" onSubmit={submit}>

      <div className="field-row">
        <div className="field">
          <label htmlFor="cs-description">What was it for?</label>
          <input
            id="cs-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={300}
            disabled={busy}
            placeholder="Joinery deposit"
          />
        </div>
        <div className="field">
          <label htmlFor="cs-reference">Invoice number</label>
          <input
            id="cs-reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            maxLength={120}
            disabled={busy}
          />
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="cs-amount">Amount ({currency})</label>
          <input
            id="cs-amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            disabled={busy}
            placeholder="20000.00"
          />
          <p className="hint">
            A credit note goes in as a negative — that is a real cost entry, not an error.
          </p>
        </div>
        <div className="field">
          <label htmlFor="cs-on">Date</label>
          <input
            id="cs-on"
            type="date"
            value={on}
            onChange={(e) => setOn(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>

      <BudgetPicker
        id="cs-line"
        value={budgetLineId}
        lines={budgetLines}
        busy={busy}
        onChange={setBudgetLineId}
      />

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !valid}>
          {busy ? 'Saving…' : 'Record it'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
